import { useEffect, useState } from "react";
import { localizeContent } from "@/i18n/translations";

export type SiteNavigationLink = {
  label: string;
  href: string;
  meta: string;
};

export type SiteFooterContent = {
  eyebrow: string;
  kicker: string;
  cta_label: string;
  cta_href: string;
  headline_lines: string[];
  body: string;
  navigation: SiteNavigationLink[];
  copyright: string;
  credit_label: string;
  credit_name: string;
  credit_url: string;
  credit_logo_url: string;
};

export type SiteSocialLink = {
  label: string;
  url: string;
};

export type SiteBrandContent = {
  brand_name: string;
  logo_url: string;
  menu_label: string;
  menu_kicker: string;
  menu_visual_url: string;
  menu_visual_position: string;
  menu_words: string[];
  primary_navigation: SiteNavigationLink[];
  social_links: SiteSocialLink[];
  footer: SiteFooterContent;
};

export const DEFAULT_SITE_BRAND: SiteBrandContent = {
  brand_name: "VenueX",
  logo_url: "/static/preview/venuex-logo-768x432.png",
  menu_label: "Menu",
  menu_kicker: "Navigation",
  menu_visual_url: "/static/venuex/live/venuex-onsite.jpg",
  menu_visual_position: "center center",
  menu_words: ["digital", "editorial", "celebration"],
  primary_navigation: [
    { href: "/", label: "Home", meta: "Overview" },
    { href: "/our-identity/", label: "Identity", meta: "Who we are" },
    { href: "/our-mission/", label: "Mission", meta: "Our purpose" },
    { href: "/service/", label: "Services", meta: "What we do" },
    { href: "/portfolio/", label: "Portfolio", meta: "Selected work" },
    { href: "/blog/", label: "Blog", meta: "Ideas & insights" },
    { href: "/contact/", label: "Contact", meta: "Start a project" },
  ],
  social_links: [
    { label: "Facebook", url: "https://www.facebook.com/p/VenueX-Events-61576992930619/" },
    { label: "Instagram", url: "https://www.instagram.com/venuex_events/" },
    { label: "LinkedIn", url: "https://www.linkedin.com/in/xenia-kimitzioglou-97292072/" },
  ],
  footer: {
    eyebrow: "Enquiry",
    kicker: "For brands, businesses and experiences designed to stand apart.",
    cta_label: "Start your project",
    cta_href: "/contact/",
    headline_lines: ["Bring the vision.", "We'll shape what comes next."],
    body:
      "VenueX brings brand identity, digital presence and live experience together under one clear and cohesive direction.",
    navigation: [
      { href: "/service/", label: "What we do", meta: "" },
      { href: "/portfolio/", label: "Selected work", meta: "" },
      { href: "/our-identity/", label: "Who we are", meta: "" },
      { href: "/contact/", label: "Start a project", meta: "" },
      { href: "/privacy-policy/", label: "Privacy Policy", meta: "" },
    ],
    copyright: "{year} VenueX. Brand Strategy, Digital Presence & Experiences.",
    credit_label: "Designed & built by",
    credit_name: "VenueX",
    credit_url: "https://venuex.gr",
    credit_logo_url: "/static/img/branding/venuex-admin-logo.png",
  },
};

let cachedSiteBrand: SiteBrandContent | null = null;
let siteBrandRequest: Promise<SiteBrandContent> | null = null;

function loadSiteBrand() {
  if (cachedSiteBrand) {
    return Promise.resolve(cachedSiteBrand);
  }

  if (!siteBrandRequest) {
    siteBrandRequest = fetch("/api/site-brand/", {
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load VenueX site branding");
        }

        return response.json() as Promise<SiteBrandContent>;
      })
      .then((content) => {
        cachedSiteBrand = localizeContent(content);
        return cachedSiteBrand;
      })
      .catch(() => localizeContent(DEFAULT_SITE_BRAND));
  }

  return siteBrandRequest;
}

export function useSiteBrand() {
  const [content, setContent] = useState(cachedSiteBrand || localizeContent(DEFAULT_SITE_BRAND));

  useEffect(() => {
    let active = true;

    loadSiteBrand().then((nextContent) => {
      if (active) {
        setContent(nextContent);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  return content;
}
